import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Ban as Bandage, PlusCircle, Clock, Star, Shield, Eye, EyeOff } from 'lucide-react';
import { PoolContribution } from './typesMyTeams';
import { useUserStore } from '../../lib/userStore';
import { translate } from '../../lib/translations';
import { usePlayerCooldown } from '../../lib/hooks/usePlayerCooldown';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';
import { useEmbedStore } from '../../lib/embedStore';

interface Goalie {
  playerid: number;
  goaliefullname: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  wins: number;
  losses: number;
  shutouts: number;
  savepct: number;
  goals: number;
  assists: number;
  isnhl: boolean;
  selection_percentage?: number;
  trading_bloc?: boolean | null;
  is_intouchable?: boolean | null;
}

interface SortConfig {
  field: keyof Goalie;
  direction: 'asc' | 'desc';
}

interface GoaliesTableProps {
  goalies: Goalie[];
  sortConfig: SortConfig;
  onSort: (field: keyof Goalie) => void;
  onRelease: (goalie: Goalie) => void;
  onToggleReserve: (goalie: Goalie) => void;
  onSwapSelect: (goalie: Goalie) => void;
  selectedGoalie: number | null;
  multiSelectMode?: boolean;
  isPlayerSelectedInMultiMode?: (goalie: Goalie) => boolean;
  language: 'en' | 'fr';
  injuredPlayers: Array<{
    playerid: number; 
    description: string; 
    returned_date?: string;
    days_remaining?: number;
    injury_unique_id?: string;
    returned_injury_unique_id?: string;
  }>;
  activeSection?: 'MAIN' | 'RESERVES' | 'INJURED' | 'SCRATCH';
  onSendToInjuredList?: (goalie: Goalie) => void;
  onReactivateFromInjured?: (goalie: Goalie) => void;
  onMoveToScratch?: (goalie: Goalie) => void;
  onRemoveFromScratch?: (goalie: Goalie) => void;
  onToggleTradingBloc?: (goalie: Goalie, status: boolean) => void;
  onToggleIntouchable?: (goalie: Goalie, isIntouchable: boolean) => void;
  onViewDetails?: (goalie: Goalie) => void;
  hideActions?: boolean;
  watchlistPlayerIds?: Set<number>;
  onAddToWatchlist?: (playerId: number) => void;
  onRemoveFromWatchlist?: (playerId: number) => void;
  watchlistActionInProgress?: boolean;
  partnerLimitedActions?: boolean;
  // Pool stats
  statsMode?: 'nhl' | 'pool';
  poolStats?: Map<number, PoolContribution>;
}

interface Column {
  field: keyof Goalie;
  translationKey: string;
  sortable?: boolean;
}

const SELECTION_PERCENTAGE_COLUMN_KEY = 'selection.percentage';

const columns: Column[] = [
  { field: 'goaliefullname', translationKey: 'name', sortable: true },
  { field: 'teamabbrevs', translationKey: 'team' },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'wins', translationKey: 'wins', sortable: true },
  { field: 'losses', translationKey: 'losses', sortable: true },
  { field: 'shutouts', translationKey: 'shutouts', sortable: true },
  { field: 'savepct', translationKey: 'save.percentage', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'salary', translationKey: 'salary', sortable: true },
];

export function GoaliesTable({
  goalies,
  sortConfig,
  onSort,
  onRelease,
  onToggleReserve,
  onSwapSelect,
  selectedGoalie,
  multiSelectMode = false,
  isPlayerSelectedInMultiMode,
  language,
  injuredPlayers = [],
  activeSection = 'MAIN',
  onSendToInjuredList,
  onReactivateFromInjured,
  onMoveToScratch,
  onRemoveFromScratch,
  onToggleTradingBloc,
  onToggleIntouchable,
  onViewDetails,
  hideActions = false,
  watchlistPlayerIds = new Set(),
  onAddToWatchlist = () => {},
  onRemoveFromWatchlist = () => {},
  watchlistActionInProgress = false,
  partnerLimitedActions = false,
  statsMode = 'nhl',
  poolStats = new Map()
}: GoaliesTableProps) {
  const { activeCooldowns, startCooldown, getRemainingTime } = usePlayerCooldown();
  const isBpmSession = useUserStore((s) => s.is_bpm_session);
  const isTslhSession = useUserStore((s) => s.is_tslh_session);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const limitedActions = isBpmSession || partnerLimitedActions || (isEmbed && !!isTslhSession);
  const [tooltipInfo, setTooltipInfo] = useState<{ x: number, y: number, playerId: number, action: string } | null>(null);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState<{ playerId: number, playerName: string, description: string, daysRemaining?: number, isReturned?: boolean } | null>(null);
  const [openActionsForId, setOpenActionsForId] = useState<number | null>(null);
  const actionsToggleRef = useRef<HTMLElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuStyle, setActionsMenuStyle] = useState<React.CSSProperties>({});

  // Watchlist handlers
  const handleAddToWatchlistClick = (playerId: number) => {
    onAddToWatchlist(playerId);
  };

  const handleRemoveFromWatchlistClick = (playerId: number) => {
    onRemoveFromWatchlist(playerId);
  };

  const isInWatchlist = (playerId: number) => {
    return watchlistPlayerIds.has(playerId);
  };

  const formatShortName = (fullName: string) => {
    const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return '';

    const parts = normalized.split(' ');
    if (parts.length < 2) return normalized;

    const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
    const lastName = parts[parts.length - 1] || '';
    if (!firstInitial || !lastName) return normalized;
    return `${firstInitial}.${lastName}`;
  };

  const closeActionsMenu = () => {
    setOpenActionsForId(null);
    actionsToggleRef.current = null;
  };

  const updateActionsMenuPosition = () => {
    const toggleEl = actionsToggleRef.current;
    const menuEl = actionsMenuRef.current;
    if (!toggleEl || !menuEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const padding = 8;

    const menuWidth = Math.max(220, menuEl.offsetWidth || 0);
    const naturalMenuHeight = menuEl.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUp = spaceBelow < naturalMenuHeight && spaceAbove > spaceBelow;

    const available = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(available, window.innerHeight - padding * 2);
    const visibleHeight = Math.min(naturalMenuHeight || maxHeight, maxHeight);

    let top = openUp ? rect.top - visibleHeight : rect.bottom;
    if (top < padding) top = padding;
    if (top + visibleHeight > window.innerHeight - padding) {
      top = window.innerHeight - padding - visibleHeight;
    }

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuWidth;
    }
    if (left < padding) left = padding;

    setActionsMenuStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 3000,
      maxHeight,
      overflowY: 'auto',
      minWidth: 220,
    });
  };

  useLayoutEffect(() => {
    if (openActionsForId === null) return;
    const raf = requestAnimationFrame(() => {
      updateActionsMenuPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [openActionsForId]);

  useEffect(() => {
    if (openActionsForId === null) return;

    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      if (actionsMenuRef.current?.contains(target)) return;
      if (actionsToggleRef.current?.contains(target)) return;
      closeActionsMenu();
    };

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeActionsMenu();
    };

    const onScrollOrResize = () => {
      updateActionsMenuPosition();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openActionsForId]);

  const sectionClass = `myteams-section-${activeSection.toLowerCase()}`;

  const visibleColumns = (statsMode === 'pool'
    ? columns.filter(c => !['losses', 'savepct'].includes(c.field))
    : columns).reduce<Column[]>((visible, column, index) => {
      visible.push(column);
      if (index === 0 && goalies.some((goalie) => goalie.selection_percentage !== undefined)) {
        visible.push({
          field: 'selection_percentage',
          translationKey: SELECTION_PERCENTAGE_COLUMN_KEY,
          sortable: true,
        });
      }
      return visible;
    }, []);

  const renderSortIcon = (field: keyof Goalie, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleViewDetailsClick = (goalie: Goalie, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.(goalie);
  };

  const handleReleaseClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    onRelease(goalie);
  };

  const handleToggleReserveClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    onToggleReserve(goalie);
  };
  
  const handleSwapSelectClick = (goalie: Goalie) => {
    if (!activeCooldowns.has(goalie.playerid)) {
      onSwapSelect(goalie);
    }
  };
  
  const handleSendToInjuredListClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    if (onSendToInjuredList) {
      onSendToInjuredList(goalie);
    }
  };
  
  const handleReactivateFromInjuredClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    if (onReactivateFromInjured) {
      onReactivateFromInjured(goalie);
    }
  };

  const handleMoveToScratchClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    if (onMoveToScratch) {
      onMoveToScratch(goalie);
    }
  };

  const handleRemoveFromScratchClick = (goalie: Goalie, e: React.MouseEvent) => {
    if (activeCooldowns.has(goalie.playerid)) {
      e.preventDefault();
      return;
    }

    startCooldown(goalie.playerid);
    if (onRemoveFromScratch) {
      onRemoveFromScratch(goalie);
    }
  };

  const handleToggleTradingBlocClick = (goalie: Goalie, status: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleTradingBloc) {
      startCooldown(goalie.playerid);
      onToggleTradingBloc(goalie, status);
    }
  };

  const handleToggleIntouchableClick = (goalie: Goalie, isIntouchable: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleIntouchable) {
      startCooldown(goalie.playerid);
      onToggleIntouchable(goalie, isIntouchable);
    }
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getGoalieImageUrl = (goalie: Goalie) => {
    if (imageErrors[goalie.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${goalie.teamabbrevs}/${goalie.playerid}.png`;
  };

  const handleMouseEnter = (e: React.MouseEvent, playerId: number, action: string) => {
    if (activeCooldowns.has(playerId)) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipInfo({
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY - 25,
        playerId,
        action
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo(null);
  };

  const isGoalieInjured = (playerId: number) => {
    const injury = injuredPlayers.find(p => p.playerid === playerId);
    if (!injury) return false;
    
    // Goalie is injured if:
    // 1. No returned_date at all (never returned), OR
    // 2. Has returned_date but injury_unique_id doesn't match returned_injury_unique_id (different injury, current one is still active)
    return !injury.returned_date || 
           (injury.injury_unique_id && injury.injury_unique_id !== injury.returned_injury_unique_id);
  };

  const isGoalieReturnedFromInjury = (playerId: number) => {
    const player = injuredPlayers.find(p => p.playerid === playerId);
    // Goalie has returned only if returned_date exists AND it matches the current injury_unique_id
    return player && player.returned_date && player.injury_unique_id === player.returned_injury_unique_id ? true : false;
  };
  
  const getDaysRemaining = (playerId: number) => {
    const player = injuredPlayers.find(p => p.playerid === playerId);
    return player && player.days_remaining !== undefined ? player.days_remaining : 0;
  };

  const getInjuryDescription = (playerId: number) => {
    const injury = injuredPlayers.find(p => p.playerid === playerId);
    return injury ? injury.description : '';
  };

  const handleShowInjuryModal = (goalie: Goalie) => {
    const isReturned = isGoalieReturnedFromInjury(goalie.playerid);
    const daysRemaining = getDaysRemaining(goalie.playerid);
    
    setSelectedInjury({
      playerId: goalie.playerid,
      playerName: goalie.goaliefullname,
      description: getInjuryDescription(goalie.playerid),
      daysRemaining: daysRemaining,
      isReturned: isReturned
    });
    setShowInjuryModal(true);
  };

  useEffect(() => {
    const handleMouseMove = () => {
      if (tooltipInfo) {
        setTooltipInfo(null);
      }
    };

    window.addEventListener('scroll', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleMouseMove);
    };
  }, [tooltipInfo]);

  return (
    <div className={`table-responsive ${sectionClass}`}>
      <table className="table table-hover table-sm align-middle myteams-table myteams-goalies">
        <thead>
          <tr>
            {!hideActions && (
              <th className="myteams-th-actions" aria-label="Actions" title="Actions"></th>
            )}
            <th>Photo</th>
            {visibleColumns.map((column) => (
              <th
                key={column.field}
                onClick={() => column.sortable && onSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {goalies.map((goalie) => (
            <tr 
              key={goalie.playerid}
              className={
                multiSelectMode && isPlayerSelectedInMultiMode?.(goalie)
                  ? 'table-success border border-success'
                  : selectedGoalie === goalie.playerid 
                    ? 'table-primary' 
                    : ''
              }
              style={
                multiSelectMode && isPlayerSelectedInMultiMode?.(goalie)
                  ? { 
                      backgroundColor: '#d1e7dd', 
                      fontWeight: 'bold',
                      boxShadow: '0 0 10px rgba(25, 135, 84, 0.3)',
                      animation: 'pulse 2s infinite'
                    }
                  : {}
              }
            >
              {!hideActions && (
                <td>
                  {/* Mobile: keep inline buttons. Tablet/Desktop: show a dropdown to save space. */}
                  <div className="myteams-actions-inline d-flex gap-2">
                    {activeSection !== 'INJURED' && (
                      <>
                        <div
                          className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'release')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => handleReleaseClick(goalie, e)}
                            disabled={activeCooldowns.has(goalie.playerid)}
                          >
                            {translate('release', language)}
                          </button>
                          
                          {activeCooldowns.has(goalie.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                              </div>
                              <div 
                                className="cooldown-progress" 
                                style={{ 
                                  width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                }}
                              ></div>
                            </>
                          )}
                        </div>
                        
                        {!limitedActions && (<>
                        <div
                          className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'toggle')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className={`btn btn-sm ${goalie.isnhl ? 'btn-warning' : 'btn-success'}`}
                            onClick={(e) => handleToggleReserveClick(goalie, e)}
                            disabled={activeCooldowns.has(goalie.playerid)}
                          >
                            {goalie.isnhl ? translate('demote', language) : translate('promote', language)}
                          </button>
                          
                          {activeCooldowns.has(goalie.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                              </div>
                              <div 
                                className="cooldown-progress" 
                                style={{ 
                                  width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                }}
                              ></div>
                            </>
                          )}
                        </div>

                        <div
                          className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                        >
                          <button
                            className={`btn btn-sm ${
                              multiSelectMode && isPlayerSelectedInMultiMode?.(goalie)
                                ? 'btn-warning active'
                                : multiSelectMode
                                  ? 'btn-outline-warning'
                                  : selectedGoalie === goalie.playerid 
                                    ? 'btn-outline-primary active' 
                                    : 'btn-outline-primary'
                            }`}
                            onClick={() => handleSwapSelectClick(goalie)}
                            disabled={activeCooldowns.has(goalie.playerid)}
                            title={language === 'fr' ? "Sélectionner pour permutation" : "Select for swapping"}
                          >
                            <RefreshCw size={16} />
                          </button>
                        </div>
                        </>) /* end !isBpmSession */}

                        <div className="player-action">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={(e) => handleViewDetailsClick(goalie, e)}
                            title={language === 'fr' ? 'Voir les détails du joueur' : 'View player details'}
                          >
                            <Eye size={16} />
                          </button>
                        </div>

                        {/* Bouton Watchlist */}
                        {!limitedActions && <div className="player-action">
                          <button
                            className={`btn btn-sm ${isInWatchlist(goalie.playerid) ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => isInWatchlist(goalie.playerid) 
                              ? handleRemoveFromWatchlistClick(goalie.playerid) 
                              : handleAddToWatchlistClick(goalie.playerid)
                            }
                            disabled={watchlistActionInProgress}
                            title={language === 'fr' 
                              ? (isInWatchlist(goalie.playerid) ? 'Retirer de la liste de surveillance' : 'Ajouter à la liste de surveillance')
                              : (isInWatchlist(goalie.playerid) ? 'Remove from watchlist' : 'Add to watchlist')
                            }
                          >
                            {isInWatchlist(goalie.playerid) ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>}

                        {/* Boutons Trading Bloc */}
                        {!limitedActions && onToggleTradingBloc && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
                          <>
                            <div
                              className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                            >
                              <button
                                className={`btn btn-sm ${goalie.trading_bloc === true ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={(e) => handleToggleTradingBlocClick(goalie, !goalie.trading_bloc, e)}
                                disabled={activeCooldowns.has(goalie.playerid)}
                                title={language === 'fr' 
                                  ? (goalie.trading_bloc === true ? "Retirer du Trading Bloc" : "Ajouter au Trading Bloc")
                                  : (goalie.trading_bloc === true ? "Remove from Trading Bloc" : "Add to Trading Bloc")
                                }
                              >
                                <Star size={14} />
                              </button>
                              
                              {activeCooldowns.has(goalie.playerid) && (
                                <>
                                  <div className="cooldown-overlay">
                                    {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                                  </div>
                                  <div 
                                    className="cooldown-progress" 
                                    style={{ 
                                      width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                    }}
                                  ></div>
                                </>
                              )}
                            </div>

                            <div
                              className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                            >
                              <button
                                className={`btn btn-sm ${goalie.is_intouchable === true ? 'btn-danger' : 'btn-outline-warning'}`}
                                onClick={(e) => handleToggleIntouchableClick(goalie, !goalie.is_intouchable, e)}
                                disabled={activeCooldowns.has(goalie.playerid)}
                                title={language === 'fr' 
                                  ? (goalie.is_intouchable === true ? "Retirer des Intouchables" : "Marquer comme Intouchable")
                                  : (goalie.is_intouchable === true ? "Remove from Untouchables" : "Mark as Untouchable")
                                }
                              >
                                <Shield size={14} />
                              </button>
                              
                              {activeCooldowns.has(goalie.playerid) && (
                                <>
                                  <div className="cooldown-overlay">
                                    {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                                  </div>
                                  <div 
                                    className="cooldown-progress" 
                                    style={{ 
                                      width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                    }}
                                  ></div>
                                </>
                              )}
                            </div>
                          </>
                        )}
                        
                        {!limitedActions && activeSection === 'MAIN' && onMoveToScratch && (
                          <div
                            className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'scratch')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={(e) => handleMoveToScratchClick(goalie, e)}
                              disabled={activeCooldowns.has(goalie.playerid)}
                              title={language === 'fr' ? "Envoyer vers Scratch" : "Send to Scratch"}
                            >
                              S
                            </button>
                            
                            {activeCooldowns.has(goalie.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                                </div>
                                <div 
                                  className="cooldown-progress" 
                                  style={{ 
                                    width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {!limitedActions && activeSection === 'SCRATCH' && onRemoveFromScratch && (
                          <div
                            className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'unscratch')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-success"
                              onClick={(e) => handleRemoveFromScratchClick(goalie, e)}
                              disabled={activeCooldowns.has(goalie.playerid)}
                              title={language === 'fr' ? "Retourner au club principal" : "Return to main club"}
                            >
                              ↩
                            </button>
                            
                            {activeCooldowns.has(goalie.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                                </div>
                                <div 
                                  className="cooldown-progress" 
                                  style={{ 
                                    width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {isGoalieInjured(goalie.playerid) && onSendToInjuredList && (
                          <div
                            className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                            onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'injure')}
                            onMouseLeave={handleMouseLeave}
                          >
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => handleSendToInjuredListClick(goalie, e)}
                              disabled={activeCooldowns.has(goalie.playerid)}
                              title="Placer sur la liste des blessés"
                            >
                              <Bandage size={16} />
                            </button>
                            
                            {activeCooldowns.has(goalie.playerid) && (
                              <>
                                <div className="cooldown-overlay">
                                  {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                                </div>
                                <div 
                                  className="cooldown-progress" 
                                  style={{ 
                                    width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    
                    {activeSection === 'INJURED' && onReactivateFromInjured && (
                      <>
                        <div
                          className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                          onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'release')}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => handleReleaseClick(goalie, e)}
                            disabled={activeCooldowns.has(goalie.playerid)}
                            title={language === 'fr' ? 'Libérer' : 'Release'}
                          >
                            {translate('release', language)}
                          </button>
                          {activeCooldowns.has(goalie.playerid) && (
                            <>
                              <div className="cooldown-overlay">
                                {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                              </div>
                              <div
                                className="cooldown-progress"
                                style={{
                                  width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%`,
                                }}
                              ></div>
                            </>
                          )}
                        </div>

                      <div
                        className={`player-action ${activeCooldowns.has(goalie.playerid) ? 'player-disabled' : ''}`}
                        onMouseEnter={(e) => handleMouseEnter(e, goalie.playerid, 'reactivate')}
                        onMouseLeave={handleMouseLeave}
                      >
                        <button 
                          className="btn btn-sm btn-success"
                          title="Retirer de la liste des blessés"
                          onClick={(e) => handleReactivateFromInjuredClick(goalie, e)}
                          disabled={activeCooldowns.has(goalie.playerid)}
                        >
                          <PlusCircle size={16} className="me-1" />
                          Réactiver
                        </button>
                        
                        {activeCooldowns.has(goalie.playerid) && (
                          <>
                            <div className="cooldown-overlay">
                              {Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s
                            </div>
                            <div 
                              className="cooldown-progress" 
                              style={{ 
                                width: `${(getRemainingTime(goalie.playerid) / 5000) * 100}%` 
                              }}
                            ></div>
                          </>
                        )}
                      </div>
                      </>
                    )}
                  </div>

                  <div className="myteams-actions-dropdown dropdown">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary dropdown-toggle myteams-actions-toggle"
                      aria-expanded={openActionsForId === goalie.playerid}
                      aria-label={language === 'fr' ? 'Actions' : 'Actions'}
                      title={language === 'fr' ? 'Actions' : 'Actions'}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                        setOpenActionsForId((prev) => (prev === goalie.playerid ? null : goalie.playerid));
                      }}
                    >
                      {/* caret is provided by Bootstrap via .dropdown-toggle::after */}
                    </button>
                    {openActionsForId === goalie.playerid &&
                      createPortal(
                        <div
                          ref={actionsMenuRef}
                          className="dropdown-menu show"
                          style={actionsMenuStyle}
                          role="menu"
                        >
                          {activeSection !== 'INJURED' && (
                            <>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReleaseClick(goalie, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(goalie.playerid)}
                              >
                                {translate('release', language)}
                                {activeCooldowns.has(goalie.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                  : ''}
                              </button>

                              {!limitedActions && <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleToggleReserveClick(goalie, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(goalie.playerid)}
                              >
                                {goalie.isnhl ? translate('demote', language) : translate('promote', language)}
                                {activeCooldowns.has(goalie.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                  : ''}
                              </button>}

                              {!limitedActions && <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleSwapSelectClick(goalie);
                                }}
                                disabled={activeCooldowns.has(goalie.playerid)}
                              >
                                {multiSelectMode
                                  ? (language === 'fr' ? 'Sélectionner (swap 4 joueurs)' : 'Select (4-player swap)')
                                  : (language === 'fr' ? 'Sélectionner pour permutation' : 'Select for swap')}
                              </button>}

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleViewDetailsClick(goalie, e as unknown as React.MouseEvent);
                                }}
                              >
                                {language === 'fr' ? 'Détails' : 'Details'}
                              </button>

                              {!limitedActions && onToggleTradingBloc && (activeSection === 'MAIN' || activeSection === 'RESERVES') && (
                                <>
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeActionsMenu();
                                      handleToggleTradingBlocClick(goalie, !goalie.trading_bloc, e as unknown as React.MouseEvent);
                                    }}
                                    disabled={activeCooldowns.has(goalie.playerid)}
                                  >
                                    {language === 'fr'
                                      ? (goalie.trading_bloc === true ? 'Retirer du Trading Bloc' : 'Ajouter au Trading Bloc')
                                      : (goalie.trading_bloc === true ? 'Remove from Trading Bloc' : 'Add to Trading Bloc')}
                                    {activeCooldowns.has(goalie.playerid)
                                      ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                      : ''}
                                  </button>

                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeActionsMenu();
                                      handleToggleIntouchableClick(goalie, !goalie.is_intouchable, e as unknown as React.MouseEvent);
                                    }}
                                    disabled={activeCooldowns.has(goalie.playerid)}
                                  >
                                    {language === 'fr'
                                      ? (goalie.is_intouchable === true ? 'Retirer des Intouchables' : 'Marquer comme Intouchable')
                                      : (goalie.is_intouchable === true ? 'Remove from Untouchables' : 'Mark as Untouchable')}
                                    {activeCooldowns.has(goalie.playerid)
                                      ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                      : ''}
                                  </button>
                                </>
                              )}

                              {!limitedActions && activeSection === 'MAIN' && onMoveToScratch && (
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleMoveToScratchClick(goalie, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(goalie.playerid)}
                                >
                                  {language === 'fr' ? 'Envoyer vers Scratch' : 'Send to Scratch'}
                                  {activeCooldowns.has(goalie.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}

                              {!limitedActions && activeSection === 'SCRATCH' && onRemoveFromScratch && (
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleRemoveFromScratchClick(goalie, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(goalie.playerid)}
                                >
                                  {language === 'fr' ? 'Retourner au club principal' : 'Return to main club'}
                                  {activeCooldowns.has(goalie.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}

                              {isGoalieInjured(goalie.playerid) && onSendToInjuredList && (
                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeActionsMenu();
                                    handleSendToInjuredListClick(goalie, e as unknown as React.MouseEvent);
                                  }}
                                  disabled={activeCooldowns.has(goalie.playerid)}
                                >
                                  {language === 'fr' ? 'Placer sur LTIR' : 'Place on LTIR'}
                                  {activeCooldowns.has(goalie.playerid)
                                    ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                    : ''}
                                </button>
                              )}
                            </>
                          )}

                          {activeSection === 'INJURED' && onReactivateFromInjured && (
                            <>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReleaseClick(goalie, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(goalie.playerid)}
                              >
                                {translate('release', language)}
                                {activeCooldowns.has(goalie.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                  : ''}
                              </button>

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeActionsMenu();
                                  handleReactivateFromInjuredClick(goalie, e as unknown as React.MouseEvent);
                                }}
                                disabled={activeCooldowns.has(goalie.playerid)}
                              >
                                {language === 'fr' ? 'Réactiver' : 'Reactivate'}
                                {activeCooldowns.has(goalie.playerid)
                                  ? ` (${Math.ceil(getRemainingTime(goalie.playerid) / 1000)}s)`
                                  : ''}
                              </button>
                            </>
                          )}

                          {!limitedActions && <>
                          <hr className="dropdown-divider" />

                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeActionsMenu();
                              if (isInWatchlist(goalie.playerid)) {
                                handleRemoveFromWatchlistClick(goalie.playerid);
                              } else {
                                handleAddToWatchlistClick(goalie.playerid);
                              }
                            }}
                            disabled={watchlistActionInProgress}
                          >
                            {language === 'fr'
                              ? (isInWatchlist(goalie.playerid) ? 'Retirer de la surveillance' : 'Surveiller ce joueur')
                              : (isInWatchlist(goalie.playerid) ? 'Remove from watchlist' : 'Watch this player')}
                          </button>
                          </>}
                        </div>,
                        document.body
                      )}
                  </div>
                </td>
              )}
              <td className="position-relative">
                <img 
                  src={getGoalieImageUrl(goalie)} 
                  alt={goalie.goaliefullname}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }}
                  className="border"
                  onError={() => handleImageError(goalie.playerid)}
                  onClick={() => onViewDetails?.(goalie)}
                />
                {isGoalieInjured(goalie.playerid) && (
                  <span 
                    className="position-absolute" 
                    style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                    onClick={() => handleShowInjuryModal(goalie)}
                  >
                    <Bandage size={16} className="text-danger" />
                  </span>
                )}
                {activeSection === 'INJURED' && isGoalieReturnedFromInjury(goalie.playerid) && (
                  <span 
                    className="position-absolute" 
                    style={{ top: '-5px', right: '-5px', cursor: 'pointer' }}
                    onClick={() => handleShowInjuryModal(goalie)}
                  >
                    <Clock size={16} className="text-warning" />
                  </span>
                )}
              </td>
              {visibleColumns.map((column) => (
                <td
                  key={`${goalie.playerid}-${column.field}`}
                  data-field={String(column.field)}
                >
                  <div className="d-flex align-items-center">
                    {column.field === 'goaliefullname' ? (
                      <div className="d-flex align-items-center gap-2">
                        {(() => {
                          const fullName = String(goalie.goaliefullname ?? '');
                          const shortName = formatShortName(fullName);
                          return (
                            <>
                              <span className="myteams-player-name-full" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(goalie)}>{fullName}</span>
                              <span className="myteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(goalie)}>{shortName}</span>
                            </>
                          );
                        })()}
                        
                        {/* Indicateur Trading Bloc */}
                        {goalie.trading_bloc === true && (
                          <span 
                            className="badge bg-success d-flex align-items-center gap-1"
                            title="Dans le Trading Bloc (Disponible)"
                            style={{ fontSize: '0.7rem' }}
                          >
                            <Star size={12} /> <span className="myteams-badge-text">Trading</span>
                          </span>
                        )}
                        
                        {/* Indicateur Intouchable */}
                        {goalie.is_intouchable === true && (
                          <span 
                            className="badge bg-danger d-flex align-items-center gap-1"
                            title="Joueur Intouchable"
                            style={{ fontSize: '0.7rem' }}
                          >
                            <Shield size={12} /> <span className="myteams-badge-text">Intouchable</span>
                          </span>
                        )}
                        
                        {isGoalieInjured(goalie.playerid) && (
                          <Bandage 
                            size={16} 
                            className="text-danger ms-2" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(goalie)}
                          />
                        )}
                        
                        {activeSection === 'INJURED' && isGoalieReturnedFromInjury(goalie.playerid) && (
                          <Clock
                            size={16}
                            className="text-warning ms-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(goalie)}
                          />
                        )}
                      </div>
                    ) : (
                      column.field === 'salary'
                        ? (() => {
                            const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(amount);

                            // Check if goalie has salary retention received
                            const hasRetention = (goalie as any).salary_retention_received_pct && (goalie as any).salary_retention_received_pct > 0;
                            
                            if (hasRetention) {
                              const retentionPct = (goalie as any).salary_retention_received_pct;
                              const retainedAmount = (goalie as any).salary_retention_received_amount || 0;
                              const effectiveSalary = goalie.salary - retainedAmount;
                              
                              return (
                                <div style={{ whiteSpace: 'nowrap' }}>
                                  <div className="text-success fw-bold">{formatCurrency(effectiveSalary)}</div>
                                  <small className="text-muted d-block" style={{ fontSize: '0.7rem', lineHeight: '1.2' }}>
                                    {formatCurrency(goalie.salary)} (-{retentionPct}%)
                                  </small>
                                </div>
                              );
                            }
                            
                            return formatCurrency(goalie.salary);
                          })()
                        : column.field === 'savepct'
                        ? goalie.savepct.toFixed(3)
                        : statsMode === 'pool' && column.field === 'wins'
                        ? (poolStats.get(goalie.playerid)?.pool_wins ?? 0)
                        : statsMode === 'pool' && column.field === 'shutouts'
                        ? (poolStats.get(goalie.playerid)?.pool_shutouts ?? 0)
                        : statsMode === 'pool' && column.field === 'goals'
                        ? (poolStats.get(goalie.playerid)?.pool_goals ?? 0)
                        : statsMode === 'pool' && column.field === 'assists'
                        ? (poolStats.get(goalie.playerid)?.pool_assists ?? 0)
                        : statsMode === 'pool' && column.field === 'gamesplayed'
                        ? (poolStats.get(goalie.playerid)?.pool_gamesplayed ?? 0)
                        : goalie[column.field]
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      
      {tooltipInfo && (
        <div
          className="cooldown-tooltip"
          style={{
            top: `${tooltipInfo.y}px`,
            left: `${tooltipInfo.x}px`,
          }}
        >
          {language === 'en' ? 'Cooldown active:' : 'Délai actif:'} {Math.ceil(getRemainingTime(tooltipInfo.playerId) / 1000)}s
        </div>
      )}

      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={selectedInjury?.playerName || ''}
        description={selectedInjury?.description || ''}
        language={language}
        daysRemaining={selectedInjury?.daysRemaining}
        isReturned={selectedInjury?.isReturned}
      />
    </div>
  );
}
